import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Lock, Calendar, Info, AlertCircle, X, Check } from "lucide-react";
import { useUsage } from "@/hooks/useUsage";

/** Compact widget for Dashboard — shows usage progress and CTA to upgrade. */
export default function UsageWidget() {
    const { usage } = useUsage();
    const navigate = useNavigate();
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [hoveredKey, setHoveredKey] = useState(null);

    if (!usage) return <div className="glass rounded-2xl p-6 text-sm text-secondary-c" data-testid="usage-widget-loading">Завантажуємо…</div>;

    if (!usage.active) {
        return (
            <section className="glass rounded-2xl p-6 text-center" data-testid="usage-widget-inactive">
                <Lock size={20} className="mx-auto text-warning-c" />
                <h3 className="mt-3 font-display font-bold text-base text-text-primary">Активна підписка не знайдена</h3>
                <p className="mt-2 text-sm text-secondary-c">Оберіть тариф, щоб отримати доступ до AutoAds.</p>
                <button onClick={() => navigate("/pricing")} className="mt-4 btn-primary pulse-glow" data-testid="usage-go-pricing">
                    Перейти до Pricing
                </button>
            </section>
        );
    }

    const consumable = (usage.limits || []).filter((l) => l.type === "consumable" || l.type === "cap");
    const info = (usage.limits || []).filter((l) => l.type === "info" || l.type === "info_days");
    const anyWarning = consumable.some((l) => l.limit > 0 && (l.used || 0) / l.limit >= 0.8);
    const planLabel = (usage.plan || "starter").replace(/^./, (c) => c.toUpperCase());

    return (
        <>
            <section className="glass rounded-2xl p-6" data-testid="usage-widget">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display font-bold text-lg text-text-primary flex items-center gap-2">
                            <TrendingUp size={18} className="text-primary-c" /> Моє використання
                        </h3>
                        <div className="mt-1 flex items-center gap-2 text-xs font-mono text-secondary-c">
                            <span className="px-2 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary-c uppercase tracking-wider">{planLabel}</span>
                            <span className="inline-flex items-center gap-1"><Calendar size={11} /> Скидання за {usage.days_until_reset} {usage.days_until_reset === 1 ? "день" : "дн."}</span>
                        </div>
                    </div>
                    {anyWarning && (
                        <button
                            onClick={() => setShowUpgrade(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-warning/40 bg-warning/10 text-warning-c text-xs font-medium hover:bg-warning/20 transition"
                            data-testid="usage-upgrade-cta"
                        >
                            <AlertCircle size={12} /> Розглянути апгрейд
                        </button>
                    )}
                </div>

                {usage.pending_plan_change && (
                    <div className="mt-4 p-3 rounded-lg border border-warning/30 bg-warning/8 text-xs text-text-primary" data-testid="usage-pending-change">
                        ⏳ Зниження тарифу до <span className="font-mono font-semibold">{usage.pending_plan_change.to_plan}</span> вступить в силу{" "}
                        <span className="font-mono">{new Date(usage.pending_plan_change.effective_at).toLocaleDateString("uk-UA")}</span>
                    </div>
                )}

                <div className="mt-5 space-y-3">
                    {consumable.map((lim) => {
                        const pct = lim.limit > 0 ? Math.min(100, (lim.used / lim.limit) * 100) : 0;
                        const exhausted = lim.limit > 0 && lim.used >= lim.limit;
                        const warning = pct >= 80 && !exhausted;
                        return (
                            <div
                                key={lim.key}
                                className="relative"
                                onMouseEnter={() => setHoveredKey(lim.key)}
                                onMouseLeave={() => setHoveredKey(null)}
                                data-testid={`usage-row-${lim.key}`}
                            >
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-text-primary inline-flex items-center gap-1">
                                        {lim.label}
                                        <Info size={10} className="text-faint-c" />
                                    </span>
                                    <span className={`font-mono ${exhausted ? "text-error-c" : warning ? "text-warning-c" : "text-secondary-c"}`}>
                                        {lim.used} <span className="text-faint-c">/</span> {lim.limit}
                                    </span>
                                </div>
                                <div className="h-1.5 bg-surface rounded-full overflow-hidden border border-border">
                                    <div
                                        className={`h-full transition-[width] duration-700 ease-out ${exhausted ? "bg-error" : warning ? "bg-warning" : "bg-primary"}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                {hoveredKey === lim.key && (
                                    <div className="absolute top-full left-0 mt-1 z-30 glass rounded-md p-2 text-[11px] text-text-primary/85 max-w-[300px] shadow-lg pointer-events-none">
                                        Скидається {new Date(usage.current_period_end).toLocaleDateString("uk-UA")}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {info.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2 text-[11px] font-mono text-secondary-c" data-testid="usage-info-row">
                        {info.map((lim) => (
                            <span key={lim.key} className="px-2 py-1 rounded border border-border">
                                {lim.label}: <span className="text-text-primary">{lim.type === "info_days" ? `кожні ${lim.limit} д.` : lim.limit}</span>
                            </span>
                        ))}
                    </div>
                )}
            </section>

            {showUpgrade && (
                <UpgradeModal
                    currentPlan={usage.plan}
                    onClose={() => setShowUpgrade(false)}
                />
            )}
        </>
    );
}

/** Reusable modal — used both by the proactive "Розглянути апгрейд" button and
 *  by `LimitGate` (when user clicks a locked button).
 */
export function UpgradeModal({ currentPlan, recommendedPlan, reason, onClose }) {
    const navigate = useNavigate();
    const [plans, setPlans] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        import("@/lib/api").then(({ api }) => api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}));
    }, []);

    const ORDER = ["starter", "growth", "scale"];
    const target = recommendedPlan || (() => {
        const idx = ORDER.indexOf(currentPlan || "starter");
        return ORDER[Math.min(idx + 1, ORDER.length - 1)];
    })();

    const handleUpgrade = async () => {
        setBusy(true);
        setErr("");
        try {
            const { api } = await import("@/lib/api");
            const { refreshUsage } = await import("@/hooks/useUsage");
            await api.post("/plan/change", { to_plan: target });
            await refreshUsage();
            onClose?.();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Не вдалося змінити тариф");
        } finally {
            setBusy(false);
        }
    };

    const targetPlan = plans?.plans?.[target];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
            onClick={onClose}
            data-testid="upgrade-modal"
        >
            <div className="glass rounded-2xl p-7 max-w-[520px] w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display font-bold text-xl text-text-primary">Розблокувати більше можливостей</h3>
                    <button onClick={onClose} className="text-secondary-c hover:text-text-primary" data-testid="upgrade-modal-close">
                        <X size={18} />
                    </button>
                </div>

                {reason && (
                    <p className="mt-3 text-sm text-text-primary/85 leading-relaxed" data-testid="upgrade-modal-reason">{reason}</p>
                )}

                {targetPlan && (
                    <div className="mt-5 p-4 rounded-xl border border-primary/40 bg-primary/8" data-testid={`upgrade-modal-plan-${target}`}>
                        <div className="flex items-baseline justify-between gap-3">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-wider text-primary-c">Рекомендовано</div>
                                <h4 className="mt-1 font-display font-bold text-2xl text-text-primary">{targetPlan.name}</h4>
                            </div>
                            <div className="text-right">
                                <div className="font-display font-bold text-2xl text-text-primary tabular-nums">
                                    €{targetPlan.price_eur_monthly}
                                </div>
                                <div className="text-[11px] font-mono text-secondary-c">за місяць</div>
                            </div>
                        </div>
                        <ul className="mt-4 space-y-1.5 text-sm">
                            {Object.entries(targetPlan.limits).map(([key, val]) => (
                                <li key={key} className="flex items-center gap-2 text-text-primary/85">
                                    <Check size={12} className="text-success-c shrink-0" />
                                    <span>{(plans.limit_labels[key] || key)}: <span className="font-mono text-text-primary">{val}{key.endsWith("_days") ? " д." : ""}</span></span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {err && <div className="mt-3 p-2 rounded-md border border-error/30 bg-error/8 text-xs text-error-c font-mono" data-testid="upgrade-modal-error">{err}</div>}

                <div className="mt-6 flex items-center justify-end gap-2 flex-wrap">
                    <button onClick={onClose} className="btn-ghost" data-testid="upgrade-modal-cancel">Можливо пізніше</button>
                    <button
                        onClick={() => navigate("/pricing")}
                        className="btn-ghost"
                        data-testid="upgrade-modal-see-all"
                    >
                        Усі тарифи
                    </button>
                    <button
                        onClick={handleUpgrade}
                        disabled={busy}
                        className={`btn-primary ${busy ? "opacity-60" : "pulse-glow"}`}
                        data-testid="upgrade-modal-confirm"
                    >
                        {busy ? "Підвищуємо…" : `Перейти на ${targetPlan?.name || target}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

// useEffect imported at top of file.